import { useState } from 'react';
import { Button, IconButton, Stack, TextField, Tooltip, MenuItem, Snackbar, Alert, Chip } from '@mui/material';
import { Add, Visibility, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { ExportMenu } from '../../../components/ExportMenu';
import { DataTable, type Column } from '../../../components/DataTable';
import { StatusBadge } from '../../../components/StatusBadge';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { ServiceTicketFormDialog } from './ServiceTicketFormDialog';
import { ServiceTicketDetailDialog } from './ServiceTicketDetailDialog';
import { serviceTicketsApi, type ServiceTicket, type CreateTicketInput, type TicketStatus } from '../../../api/serviceTickets.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const STATUSES: TicketStatus[] = ['open', 'assigned', 'scheduled', 'in_progress', 'diagnosed', 'pending_spare', 'resolved', 'closed', 'reopened', 'cancelled'];

export function ServiceTicketListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<ServiceTicket | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['service-tickets', page, limit, status],
    queryFn: () => serviceTicketsApi.list({ page, limit, status: status || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['service-tickets'] });

  const createMut = useMutation({
    mutationFn: (input: CreateTicketInput) => serviceTicketsApi.create(input),
    onSuccess: (t) => { setFormOpen(false); invalidate(); setDetailId(t.id); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => serviceTicketsApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<ServiceTicket>[] = [
    { key: 'ticketCode', label: 'Ticket', width: 160 },
    { key: 'ticketType', label: 'Type', render: (r) => r.ticketType.replace(/_/g, ' ') },
    { key: 'priority', label: 'Priority', render: (r) => <Chip size="small" label={r.priority} color={r.priority === 'critical' ? 'error' : 'default'} /> },
    { key: 'warrantyValid', label: 'Warranty', render: (r) => (r.warrantyValid ? 'In warranty' : r.amcValid ? 'AMC' : '—') },
    { key: 'slaBreached', label: 'SLA', render: (r) => (r.slaBreached ? <Chip size="small" color="error" label="breached" /> : 'OK') },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader
        title="Service Tickets"
        subtitle="EVCPD service CRM: complaint → ticket → assign → visit → diagnose → spare → resolve → confirm → close."
        action={
          <Stack direction="row" spacing={1}>
            <ExportMenu dataset="service-tickets" permission="service_ticket.export" filters={{ status }} />
            {hasPermission('service_ticket.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setFormError(null); setFormOpen(true); }}>New Ticket</Button>}
          </Stack>
        }
      />
      <Stack direction="row" mb={2}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 200 }}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
            {hasPermission('service_ticket.delete') && ['open', 'cancelled'].includes(r.status) && (
              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>
            )}
          </>
        )}
      />
      <ServiceTicketFormDialog open={formOpen} error={formError} onClose={() => setFormOpen(false)} onSubmit={async (input) => { setFormError(null); await createMut.mutateAsync(input); }} />
      <ServiceTicketDetailDialog ticketId={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} />
      <ConfirmDialog open={Boolean(deleting)} title="Delete ticket" message={`Delete ${deleting?.ticketCode}?`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
